/**
 * How a person is named on screen (figure S3's rail).
 *
 * The review caught the two ways this goes wrong: the Owner row printed the raw
 * login `bob-inventor@test.innovateflow.com.au`, and a viewer with no stored
 * name printed `User 895e4448` — an id shown to a reader as if it were a
 * person, on the one card that answers "who can see my invention".
 *
 * The rule, in order: the name the record carries; failing that the email's
 * local part, written the way a name is written (`bob.inventor` → "Bob
 * Inventor"), because that is a real fact about the account rather than an
 * invention; failing that, an honest phrase. Never an id, and never a bare
 * login.
 */
export interface PersonLike {
  name?: string | null
  email?: string | null
  id?: string | null
}

const GROUP_PREFIX = 'group:'
const EXTORG_PREFIX = 'extorg:'

export function personLabel(person: PersonLike, fallback = 'Someone you shared with'): string {
  const name = (person.name ?? '').trim()
  if (name) return name

  const email = (person.email ?? '').trim()
  if (email.includes('@')) {
    const local = email.split('@')[0]
    const words = local
      .split(/[._-]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    if (words.length > 0) return words.join(' ')
  }
  if (email) return email

  // Groups and external organisations name themselves in their id; a bare user
  // id names nothing, so it is never shown.
  const id = (person.id ?? '').trim()
  if (id.startsWith(GROUP_PREFIX)) return `Group: ${id.slice(GROUP_PREFIX.length)}`
  if (id.startsWith(EXTORG_PREFIX)) return `Organisation: ${id.slice(EXTORG_PREFIX.length)}`
  return fallback
}


/** The signed-in viewer, for the rule below. */
export interface Viewer {
  id?: string | null
  name?: string | null
}

/**
 * The SAME name for the same person, wherever they appear.
 *
 * Round 4 regression: /project/:id's Project details read "Bob Inventor" while
 * the workspace switcher two hundred pixels away read "Bob Marsh · Inventor" —
 * one person, two names, both derived from something real, which is worse than
 * either alone. The cause was two resolvers: the switcher reads the recorded
 * display name off the signed-in token, while this file fell back to the
 * email's local part whenever the record it was handed carried no name.
 *
 * So when the person IS the viewer, the answer is the viewer's own recorded
 * name — the identical string the switcher shows — and the local-part rule is
 * never reached. For anybody else the rules below are unchanged: the record's
 * name, then the local part, then an honest phrase, and never an id.
 */
export function personLabelFor(
  person: PersonLike,
  viewer?: Viewer | null,
  fallback?: string,
): string {
  const viewerId = (viewer?.id ?? '').trim()
  const personId = (person.id ?? '').trim()
  if (viewerId && personId && viewerId === personId) {
    const own = (viewer?.name ?? '').trim()
    if (own) return own
  }
  return fallback === undefined ? personLabel(person) : personLabel(person, fallback)
}
